import { cn } from "@somethings-ui/core";
import { Skeleton } from "@somethings-ui/web";
import { FileText } from "lucide-react";
import { DateTime } from "luxon";
import { useGetConversationSummary } from "@/lib/api/mentorships";

const EMPTY_COPY =
  "An AI-generated summary of your mentee will appear here after a few conversations. It builds over time.";

export function ContextBrief({ mentorshipId }: { mentorshipId: string | null }) {
  const { data, isLoading, isError, dataUpdatedAt } = useGetConversationSummary(
    mentorshipId ?? "",
  );
  const summary = data?.data?.summary?.trim() ?? "";
  const hasSummary = !isLoading && !isError && summary.length > 0;
  const relative =
    hasSummary && dataUpdatedAt > 0
      ? DateTime.fromMillis(dataUpdatedAt).toRelative()
      : null;

  return (
    <section className="px-4 pt-4">
      <div
        className={cn(
          "flex flex-col gap-2 rounded-[20px] border bg-white p-4",
          hasSummary
            ? "border-solid border-surfaces-divider"
            : "border-dashed border-surfaces-divider",
        )}
      >
        <div className="flex items-center gap-1.5">
          <FileText
            className={cn(
              "size-3",
              hasSummary ? "text-text-tertiary" : "text-text-placeholder",
            )}
          />
          <span
            className={cn(
              "font-body text-body-4",
              hasSummary ? "text-text-tertiary" : "text-text-placeholder",
            )}
          >
            {relative ? `Summary — updated ${relative}` : "Summary"}
          </span>
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ) : hasSummary ? (
          <p className="whitespace-pre-line font-body text-body-2 text-text-secondary">
            {summary}
          </p>
        ) : (
          <p className="font-body text-body-2 text-text-placeholder">
            {EMPTY_COPY}
          </p>
        )}
      </div>
    </section>
  );
}
